import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Observable, catchError, forkJoin, throwError } from 'rxjs'
import { environment } from 'src/environments/environment'
import { AuthService } from '../common/auth.service'

@Injectable({
  providedIn: 'root'
})
export class WebService {
  private baseURL = environment.baseUrl

  constructor(private http: HttpClient) {}

  private addHeader = (headers: HttpHeaders, headerName: string, headerValue: string) => {
    headers = headers || new HttpHeaders()
    return headers.set(headerName, headerValue)
  }

  private addContentTypeAppJSONHeader = (headers: HttpHeaders) => {
    return this.addHeader(headers, 'Content-Type', 'application/json')
  }

  private addContentTypeTextPlainHeader = (headers: HttpHeaders) => {
    return this.addHeader(headers, 'Content-Type', 'text/plain')
  }

  private addAuthHeader = (headers: HttpHeaders) => {
    const token = AuthService.getToken()
    return this.addHeader(headers, 'Authorization', `Bearer ${token}`)
  }

  private addRefreshHeader = (headers: HttpHeaders) => {
    const token = AuthService.getRefreshToken()
    return this.addHeader(headers, 'refreshToken', `${token}`)
  }

  private getRequestOptions = (headers: HttpHeaders, body: object = null) => {
    return {
      headers: this.addAuthHeader(headers),
      body: body
    }
  }

  private createEndpointURL = (endPoint: string): string => {
    return this.baseURL + endPoint
  }

  get = (endPoint: string, queryParams?: object[], headers?: HttpHeaders): Observable<any> => {
    headers = this.addAuthHeader(headers)
    return this.http
      .get(this.createEndpointURL(endPoint), { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  getWithRefresh = (endPoint: string, queryParams?: object[], headers?: HttpHeaders): Observable<any> => {
    headers = this.addRefreshHeader(headers)
    return this.http
      .get(this.createEndpointURL(endPoint), { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  getWithAuthTokenAndRefresh = (endPoint: string, queryParams?: object[], headers?: HttpHeaders): Observable<any> => {
    headers = this.addRefreshHeader(headers)
    headers = this.addAuthHeader(headers)
    return this.http
      .get(this.createEndpointURL(endPoint), { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  getWithoutAuth = (endPoint: string, queryParams?: object[], headers?: HttpHeaders): Observable<any> => {
    return this.http
      .get(this.createEndpointURL(endPoint), { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  put = (
    endPoint: string,
    body: object,
    headers: HttpHeaders = null,
    queryParams: object[] = [],
    addContentTypeHeader = true
  ): Observable<any> => {
    if (addContentTypeHeader) {
      headers = this.addContentTypeAppJSONHeader(headers)
    }
    headers = this.addAuthHeader(headers)
    return this.http
      .put<any>(this.createEndpointURL(endPoint), body, { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  putWithoutAuth = (
    endPoint: string,
    body: object,
    headers: HttpHeaders = null,
    queryParams: object[] = []
  ): Observable<any> => {
    headers = this.addContentTypeAppJSONHeader(headers)
    return this.http
      .put<any>(this.createEndpointURL(endPoint), body, { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  post = (
    endPoint: string,
    body: object,
    headers: HttpHeaders = null,
    queryParams: object[] = [],
    addContentTypeHeader = true
  ): Observable<any> => {
    if (addContentTypeHeader) {
      headers = this.addContentTypeAppJSONHeader(headers)
    }
    headers = this.addAuthHeader(headers)
    let params = new HttpParams()
    if (queryParams) {
      queryParams.forEach((query, index) => (params = this.addParam(params, query['key'], query['value'])))
    }
    return this.http
      .post<any>(this.createEndpointURL(endPoint), body, { headers, params })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  postWithAuthTokenAndRefresh = (
    endPoint: string,
    body: object,
    headers: HttpHeaders = null,
    queryParams: object[] = []
  ): Observable<any> => {
    headers = this.addRefreshHeader(headers)
    return this.post(endPoint, body, headers, queryParams)
  }

  postWithoutAuth(
    endPoint: string,
    body: any,
    headers: HttpHeaders = null,
    queryParams: object[] = [],
    isFormData = false,
    isText = false
  ): Observable<any> {
    if (!isFormData) {
      headers = this.addContentTypeAppJSONHeader(headers)
    }
    if (isText) {
      headers = this.addContentTypeTextPlainHeader(headers)
    }

    return this.http.post<any>(this.createEndpointURL(endPoint), body, { headers })
  }

  delete = (
    endPoint: string,
    body: object = null,
    headers: HttpHeaders = null,
    queryParams: object[] = []
  ): Observable<any> => {
    const options = this.getRequestOptions(headers, body)

    return this.http
      .delete(this.createEndpointURL(endPoint), options)
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  downloadFileGET = (endPoint: string, headers?: HttpHeaders): Observable<any> => {
    headers = this.addAuthHeader(headers)
    return this.http
      .get(this.createEndpointURL(endPoint), {
        headers,
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  downloadFilePOST = (endPoint: string, queryParams: object[], body, headers?: HttpHeaders): Observable<any> => {
    headers = this.addAuthHeader(headers)
    let params = new HttpParams()
    if (queryParams) {
      queryParams.forEach((query, index) => (params = this.addParam(params, query['key'], query['value'])))
    }
    return this.http
      .post(this.createEndpointURL(endPoint), body, { headers, params, observe: 'response', responseType: 'blob' })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  uploadDocument = (endPoint: string, body: object, headers: HttpHeaders = null): Observable<any> => {
    headers = this.addAuthHeader(headers)

    return this.http
      .post<any>(this.createEndpointURL(endPoint), body, { headers })
      .pipe(catchError((error) => throwError(() => new Error(error))))
  }

  makeParallelPostAPICalls(data: any[], endPoints: string[]): Observable<any[]> {
    const headers = this.addAuthHeader(null)
    const observables = endPoints.map((endPoint, index) =>
      this.http.post(this.createEndpointURL(endPoint), data[index], { headers })
    )
    return forkJoin(observables)
  }

  addParam = (params: HttpParams, paramName: string, paramValue: string) => {
    params = params || new HttpParams()
    return params.set(paramName, paramValue)
  }
}
